import axios from "axios";
import { isAuthenticated } from "@/services/auth";

const API_BASE_URL = "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // send HttpOnly cookies (auth_token) with every request
});

// Handler for 401 errors - will be set by the app via setupAuthErrorHandler()
let authErrorHandler: ((message: string) => void) | null = null;

/**
 * Set up the authentication error handler
 * This is called from the app layout/providers to connect the API service
 * with the AuthErrorContext
 */
export const setupAuthErrorHandler = (handler: (message: string) => void) => {
  authErrorHandler = handler;
};

// Request interceptor: block all API calls when not authenticated.
// This prevents pages from hitting the backend before the auth redirect fires.
api.interceptors.request.use(
  (config) => {
    if (typeof window !== "undefined" && !isAuthenticated()) {
      // Abort the request with a clear controller to create AbortError
      // This will be caught in response interceptor and trigger auth handler
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
    }
    return config;
  },
  (error) => {
    // If request interceptor itself fails, still reject
    return Promise.reject(error);
  },
);

// Handle 401 errors AND auth-related aborts (expired/invalid token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (typeof window === "undefined") {
      return Promise.reject(error);
    }
    // Check for 401 response OR AbortError (aborted due to missing auth)
    const is401 = error.response?.status === 401;
    const isAbortError = error.code === "ERR_CANCELED" && error.message === "Request aborted";
    if ((is401 || isAbortError) && typeof window !== "undefined") {
      // Clear the stale display-only profile from localStorage.
      localStorage.removeItem("auth_user");

      const currentPath = window.location.pathname;
      const isAuthPage = currentPath === "/" || currentPath === "/unauthorized";

      // Only trigger error handler on non-auth pages
      if (!isAuthPage) {
        if (authErrorHandler) {
          // If handler is set (via context), use it to show UnauthorizedPage
          const message = is401 
            ? "Your session has expired. Please log in again."
            : "Authorization required. Please log in.";
          authErrorHandler(message);
        } else {
          // Fallback: redirect to home if handler is not set
          window.location.href = "/";
        }
      }
    }
    return Promise.reject(error);
  },
);

// Vehicle APIs to check vechile already exists or not
export const getVehicleByNumber = async (vehicleNumber: string) => {
  const response = await api.get(`/parking/getVehicleDetails/${vehicleNumber}`);
  return response.data;
};

// Get list of available timezones
export const getTimezones = async () => {
  const response = await api.get("/utility/timezones");
  return response.data;
};

// // Get vehicle details with parking history
// export const getVehicleDetailsWithHistory = async (
//   vehicleNumber: string,
//   page: number = 1,
// ) => {
//   try {
//     // Get basic vehicle details
//     const vehicleDetailsResponse = await api.get(
//       `/parking/getVehicleDetails/${vehicleNumber}`,
//     );

//     // Get parking history for this vehicle
//     const historyResponse = await api.get("/parking/list", {
//       params: {
//         vehicleNumber: vehicleNumber,
//         page: page,
//         filter: "all",
//         status: "all",
//       },
//     });

//     return {
//       vehicleDetails: vehicleDetailsResponse.data,
//       history: historyResponse.data,
//     };
//   } catch (error) {
//     console.error("Error fetching vehicle details with history:", error);
//     throw error;
//   }
// };

// Parking APIs to check-in vehicles
export const checkIn = async (data: any) => {
  const response = await api.post("/parking/check-in", data);
  return response.data;
};

// Parking APIs to check-out vehicles
export const checkOut = async (data: any) => {
  const response = await api.post("/parking/check-out", data);
  return response.data;
};

// to get details of active parking by vehicle number or token id there, rate amount and all
export const getActiveEntry = async (parkingEntryId?: string) => {
  const params: any = {};
  if (parkingEntryId) params.parkingEntryId = parkingEntryId;
  // if (tokenId) params.tokenId = tokenId;
  const response = await api.get(
    `/parking/getParkingDetails/${parkingEntryId}`,
  );
  return response.data;
};

export const printReceipt = async (parkingEntryId?: string) => {
  const params: any = {};
  if (parkingEntryId) params.parkingEntryId = parkingEntryId;
  const response = await api.get(`/parking/print-reciept/${parkingEntryId}`);
  return response.data;
};

// /getParkingDetails/:parkingEntryId

export const getDashboardStats = async (
  filter: "allTime" | "today" | "week" | "month" | "custom",
  startDate?: string,
  endDate?: string,
) => {
  let params: any = {};
  if (startDate && endDate) {
    params = { filter, from: startDate, to: endDate };
  } else {
    params = { filter };
  }
  const response = await api.get("/dashboard", { params });
  const { summary, trends, vehicleDistribution, topCustomers } = response.data;
  // Transform revenue trend data
  const revenueChart = (trends?.revenue || []).map((item: any) => ({
    label: item._id,
    gross: Math.round(item.grossRevenue || 0),
    discount: Math.round(item.discount || 0),
    net: Math.round(item.netRevenue || 0),
  }));
  // Transform parking trend data
  const parkingChart = (trends?.parking || []).map((item: any) => ({
    label: item.date,
    checkIns: item.checkIns || 0,
    checkOuts: item.checkOuts || 0,
  }));
  // Vehicle distribution
  const vehicleTypes = (vehicleDistribution || []).map((item: any) => ({
    name: item._id,
    value: item.count,
  }));
  // Map summary data
  const stats = {
    currentlyParked: summary?.currentlyParked || 0,
    checkedOut: summary?.checkedOut || 0,
    checkedIn: summary?.checkIns || 0,
    grossRevenue: Math.round(summary?.grossRevenue || 0),
    totalDiscount: Math.round(summary?.totalDiscount || 0),
    netRevenue: Math.round(summary?.netRevenue || 0),
    totalCollected: Math.round(summary?.totalCollected || 0),
    totalLended: Math.round(summary?.totalLended || 0),
    avgDurationMins: summary?.avgParkingMinutes || 0,
    avgSlotRevenue: summary?.avgSlotRevenue || 0,
    totalAvailableSpace: summary?.totalAvailableSpace || 100,
  };
  return {
    stats,
    revenueChart,
    parkingChart,
    vehicleTypes,
    topCustomers: topCustomers || { topByRevenue: [], topRepeaters: [], topDebtors: [] },
  };
};

export default api;

// services/api.ts

export const getAllVehiclesList = async (params?: {
  filter?: "all" | "today" | "week" | "month" | "custom";
  from?: string;
  to?: string;
  vehicleNumber?: string;
  driverMobile?: string;
  ownerMobile?: string;
  status?: "IN" | "OUT" | "all";
  page?: number;
  cursor?: string;
}) => {
  try {
    const response = await api.get("/parking/list", {
      params: {
        filter: params?.filter,
        from: params?.filter === "custom" ? params?.from : undefined,
        to: params?.filter === "custom" ? params?.to : undefined,
        vehicleNumber: params?.vehicleNumber,
        driverMobile: params?.driverMobile,
        ownerMobile: params?.ownerMobile,
        status: params?.status !== "all" ? params?.status : undefined,
        page: params?.page || 1,
        cursor: params?.cursor,
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// get vechile details on vechile details page  eg . each vechiles total entry details and all
export const getVehiclesDetails = async (vehicleNumber: string) => {
  const response = await api.get(
    `/parking/allparking/vehicle/${vehicleNumber}`,
    {},
  );
  return response.data;
};

// ============================================
// TENANT MANAGEMENT APIs
// ============================================

// Create a new tenant organization
export const createTenant = async (data: {
  name: string;
  code: string;
  plan?: string;
  totalAvailableSpace: number;
  businessType?: 'PARKING' | 'RAILWAY_CLOAK_ROOM' | 'ROAD_TAX';
  settingsData?: {
    logoUrl?: string;
    address?: string;
    contactNumbers?: string[];
    receiptTerms?: string[];
    billTerms?: string[];
  };
}) => {
  const response = await api.post("/tenants", data);
  return response.data;
};

/* ----------------------------- Cloak Room ---------------------------- */
export const cloakRoomCheckIn = async (data: {
  mobileNumber: string;
  depositorName: string;
  items: Array<{ itemTypeId: string; count: number }>;
  itemDescription?: string;
  planTypeId: string;
}) => {
  const response = await api.post("/cloak-room/checkin", data);
  return response.data;
};

export const cloakRoomCheckOut = async (data: {
  tokenNumber: number;
  discountAmount?: number;
  finalAmount?: number;
  collectNow?: number;
  paymentMethod?: string;
}) => {
  const response = await api.post("/cloak-room/checkout", data);
  return response.data;
};

export const listCloakRoomEntries = async (params?: Record<string, string | number | undefined>) => {
  const response = await api.get("/cloak-room/list", { params });
  return response.data;
};

export const getCloakRoomEntryByToken = async (token: number | string) => {
  const response = await api.get(`/cloak-room/token/${token}`);
  return response.data;
};

export const previewCloakRoomBill = async (token: number | string) => {
  const response = await api.get(`/cloak-room/preview/${token}`);
  return response.data;
};

export const getCloakRoomDepositorByMobile = async (mobileNumber: string) => {
  const response = await api.get(`/cloak-room/depositor/${encodeURIComponent(mobileNumber)}`);
  return response.data;
};

export const listCloakRoomItemTypes = async (tenantId?: string) => {
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = tenantId;
  const response = await api.get("/cloak-room/item-types", { params });
  return response.data;
};

export const createCloakRoomItemType = async (data: { name: string; description?: string; tenantId?: string }) => {
  const response = await api.post("/cloak-room/item-types", data);
  return response.data;
};

export const updateCloakRoomItemType = async (id: string, data: Record<string, unknown>, tenantId?: string) => {
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = tenantId;
  const response = await api.patch(`/cloak-room/item-types/${id}`, data, { params });
  return response.data;
};

export const deleteCloakRoomItemType = async (id: string, tenantId?: string) => {
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = tenantId;
  const response = await api.delete(`/cloak-room/item-types/${id}`, { params });
  return response.data;
};

export const listCloakRoomPricing = async () => {
  const response = await api.get("/cloak-room/pricing");
  return response.data;
};

export const createCloakRoomPricing = async (data: Record<string, unknown>) => {
  const response = await api.post("/cloak-room/pricing", data);
  return response.data;
};

export const updateCloakRoomPricing = async (id: string, data: Record<string, unknown>) => {
  const response = await api.patch(`/cloak-room/pricing/${id}`, data);
  return response.data;
};

export const deleteCloakRoomPricing = async (id: string) => {
  const response = await api.delete(`/cloak-room/pricing/${id}`);
  return response.data;
};

/* ------------------------------ Road Tax ----------------------------- */
export const collectRoadTax = async (data: {
  vehicleNumber: string;
  vehicleTypeId: string;
  planTypeId?: string;
  discountAmount?: number;
  collectNow?: number;
  paymentMethod?: string;
}) => {
  const response = await api.post("/road-tax/collect", data);
  return response.data;
};

export const getRoadTaxStatus = async (vehicleNumber: string) => {
  const response = await api.get(`/road-tax/status/${encodeURIComponent(vehicleNumber)}`);
  return response.data;
};

export const getRoadTaxPreview = async (vehicleTypeId: string) => {
  const response = await api.get("/road-tax/preview", { params: { vehicleTypeId } });
  return response.data;
};

export const listRoadTaxPayments = async (params?: Record<string, string | number | undefined>) => {
  const response = await api.get("/road-tax/payments", { params });
  return response.data;
};

// Returns the tenant associated with the authenticated user's JWT.
// The tenantId is never taken from client state — the backend derives it from
// the token, eliminating the IDOR risk from mutable localStorage data.
export const getMyTenant = async () => {
  const response = await api.get("/tenants/me");
  return response.data;
};

export const getTenantById = async (tenantId: string) => {
  const response = await api.get(`/tenants/${tenantId}`);
  return response.data;
};

// Get list of all tenant organizations with billing info
export const listTenants = async () => {
  const response = await api.get("/tenants");
  return response.data;
};

// Disable a tenant organization
export const disableTenant = async (tenantId: string) => {
  const response = await api.patch(`/tenants/${tenantId}/disable`);
  return response.data;
};

// Enable a tenant organization
export const enableTenant = async (tenantId: string) => {
  const response = await api.patch(`/tenants/${tenantId}/enable`);
  return response.data;
};

// Update a tenant organization
export const updateTenant = async (
  tenantId: string,
  data: {
    name?: string;
    plan?: string;
    totalAvailableSpace?: number;
    logoUrl?: string;
    address?: string;
    contactNumbers?: string[];
    receiptTerms?: string[];
    billTerms?: string[];
    isOwnerFieldsEnabled?: boolean;
    isDriverFieldsEnabled?: boolean;
  },
) => {
  const response = await api.patch(`/tenants/${tenantId}`, data);
  return response.data;
};

// ============================================
// FEATURE MANAGEMENT APIs
// ============================================

// Get all available features
export const listFeatures = async () => {
  const response = await api.get("/subscriptions/features/list");
  return response.data;
};

// Create a new feature (admin only)
export const createFeature = async (data: {
  featureKey: string;
  name: string;
  description?: string;
  type: "boolean" | "limit" | "metered";
  defaultValue?: any;
  category?: string;
}) => {
  const response = await api.post("/subscriptions/features/create", data);
  return response.data;
};

// Update a feature (admin only)
export const updateFeature = async (
  featureKey: string,
  data: {
    name?: string;
    description?: string;
    defaultValue?: any;
    category?: string;
    isActive?: boolean;
  },
) => {
  const response = await api.patch(
    `/subscriptions/features/update/${featureKey}`,
    data,
  );
  return response.data;
};

// Delete a feature (admin only)
export const deleteFeature = async (featureKey: string) => {
  const response = await api.delete(
    `/subscriptions/features/delete/${featureKey}`,
  );
  return response.data;
};

// Add feature override for tenant
export const addFeatureOverride = async (data: {
  tenantId: string;
  featureKey: string;
  enabled?: boolean;
  value?: any;
}) => {
  const response = await api.post("/subscriptions/overrides/feature", data);
  return response.data;
};

// ============================================
// SUBSCRIPTION MANAGEMENT APIs
// ============================================

// Get available plans
export const listPlans = async () => {
  const response = await api.get("/subscriptions/plans/list");
  return response.data;
};

// Create a new subscription plan
export const createPlan = async (data: {
  planKey: string;
  name: string;
  price: number;
  billingCycle: "monthly" | "yearly" | "lifetime";
  isActive?: boolean;
  features?: string[];
}) => {
  const response = await api.post("/subscriptions/plans/create", data);
  return response.data;
};

// Update an existing subscription plan
export const updatePlan = async (
  planKey: string,
  data: {
    name?: string;
    price?: number;
    billingCycle?: "monthly" | "yearly" | "lifetime";
    isActive?: boolean;
    features?: string[];
  },
) => {
  const response = await api.patch(
    `/subscriptions/plans/update/${planKey}`,
    data,
  );
  return response.data;
};

// Delete a subscription plan
export const deletePlan = async (planKey: string) => {
  const response = await api.delete(`/subscriptions/plans/delete/${planKey}`);
  return response.data;
};

// Get features attached to a plan
export const getPlanFeatures = async (planKey: string) => {
  const response = await api.get(`/subscriptions/plans/feature`, {
    params: { planKey },
  });
  return response.data;
};

// // Instruction Set API functions
// export const listInstructionSets = async () => {
//   try {
//     const response = await api.get("/system/instruction-sets");
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching instruction sets:", error);
//     throw error;
//   }
// };

// export const createInstructionSet = async (instructionData: any) => {
//   try {
//     const response = await api.post(
//       "/system/instruction-sets",
//       instructionData,
//     );
//     return response.data;
//   } catch (error) {
//     console.error("Error creating instruction set:", error);
//     throw error;
//   }
// };

// export const updateInstructionSet = async (
//   id: string,
//   instructionData: any,
// ) => {
//   try {
//     const response = await api.put(
//       `/system/instruction-sets/${id}`,
//       instructionData,
//     );
//     return response.data;
//   } catch (error) {
//     console.error("Error updating instruction set:", error);
//     throw error;
//   }
// };

// export const deleteInstructionSet = async (id: string) => {
//   try {
//     const response = await api.delete(`/system/instruction-sets/${id}`);
//     return response.data;
//   } catch (error) {
//     console.error("Error deleting instruction set:", error);
//     throw error;
//   }
// };

// ============================================
// PRICING PLAN MANAGEMENT APIs
// ============================================

// Get all registered rule types
export const listRuleTypes = async () => {
  const response = await api.get("/pricing-plans/rule-types");
  return response.data;
};

// List all pricing plans for current tenant
export const listPricingPlans = async () => {
  const response = await api.get("/pricing-plans");
  return response.data;
};

// Get a specific pricing plan by ID
export const getPricingPlanById = async (planId: string) => {
  const response = await api.get(`/pricing-plans/${planId}`);
  return response.data;
};

// Create a new pricing plan
export const createPricingPlan = async (data: {
  name: string;
  vehicleType?: string | null;
  rules: Array<{
    type: string;
    config: Record<string, any>;
    priority: number;
    isActive?: boolean;
  }>;
  roundingStrategy?: "ceil" | "floor" | "exact";
}) => {
  const response = await api.post("/pricing-plans", data);
  return response.data;
};

// Update an existing pricing plan
export const updatePricingPlan = async (
  planId: string,
  data: {
    name?: string;
    vehicleType?: string | null;
    rules?: Array<{
      type: string;
      config: Record<string, any>;
      priority: number;
      isActive?: boolean;
    }>;
    roundingStrategy?: "ceil" | "floor" | "exact";
  },
) => {
  const response = await api.put(`/pricing-plans/${planId}`, data);
  return response.data;
};

// Delete (deactivate) a pricing plan
export const deletePricingPlan = async (planId: string) => {
  const response = await api.delete(`/pricing-plans/${planId}`);
  return response.data;
};

// Simulate pricing for a given scenario
export const simulatePricing = async (data: {
  entryTime: string;
  exitTime: string;
  vehicleType: string;
  subscription?: {
    status: string;
    startDate: string;
    expiryDate: string;
  } | null;
}) => {
  const response = await api.post("/pricing-plans/simulate", data);
  return response.data;
};

// ============================================
// PRICING PLAN TYPE APIs (catalog: HOURLY, DAILY, FLAT, SLAB, MONTHLY)
// ============================================

export const listPricingPlanTypes = async (includeInactive = false) => {
  const params = includeInactive ? { includeInactive: true } : {};
  const response = await api.get("/pricing-plan-types", { params });
  return response.data;
};

export const createPricingPlanType = async (data: {
  name: string;
  unit: "hour" | "day" | "flat" | "slab" | "subscription";
  requiresCheckoutCalc?: boolean;
  allowCustomVehicle?: boolean;
}) => {
  const response = await api.post("/pricing-plan-types", data);
  return response.data;
};

export const updatePricingPlanType = async (
  id: string,
  data: {
    name?: string;
    unit?: string;
    requiresCheckoutCalc?: boolean;
    allowCustomVehicle?: boolean;
    isActive?: boolean;
  },
) => {
  const response = await api.put(`/pricing-plan-types/${id}`, data);
  return response.data;
};

export const deletePricingPlanType = async (id: string) => {
  const response = await api.delete(`/pricing-plan-types/${id}`);
  return response.data;
};

// ============================================
// VEHICLE TYPE APIs (catalog: car, bike, truck, etc.)
// ============================================

export const listVehicleTypes = async (
  includeInactive = false,
  tenantId?: string,
) => {
  const params: Record<string, any> = {};
  if (includeInactive) params.includeInactive = true;
  if (tenantId) params.tenantId = tenantId;
  const response = await api.get("/vehicle-types", { params });
  return response.data;
};

export const createVehicleType = async (data: {
  name: string;
  tenantId?: string;
}) => {
  const response = await api.post("/vehicle-types", data);
  return response.data;
};

export const updateVehicleType = async (
  id: string,
  data: { name?: string; isActive?: boolean; tenantId?: string },
) => {
  const response = await api.put(`/vehicle-types/${id}`, data);
  return response.data;
};

export const deleteVehicleType = async (id: string, tenantId?: string) => {
  const params: Record<string, any> = {};
  if (tenantId) params.tenantId = tenantId;
  const response = await api.delete(`/vehicle-types/${id}`, { params });
  return response.data;
};

// ============================================
// TENANT PLAN ACCESS APIs (which plan types a tenant can use)
// ============================================

export const listTenantPlanAccess = async (tenantId?: string) => {
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = tenantId;
  const response = await api.get("/tenant-plan-access", { params });
  return response.data;
};

export const assignTenantPlanAccess = async (data: {
  planTypeId: string;
  isDefault?: boolean;
  tenantId?: string;
}) => {
  const response = await api.post("/tenant-plan-access", data);
  return response.data;
};

export const bulkAssignTenantPlanAccess = async (
  plans: Array<{ planTypeId: string; isDefault?: boolean }>,
  tenantId?: string,
) => {
  const response = await api.post("/tenant-plan-access/bulk", {
    plans,
    tenantId,
  });
  return response.data;
};

export const updateTenantPlanAccess = async (
  id: string,
  data: { isDefault?: boolean; isActive?: boolean },
  tenantId?: string,
) => {
  const response = await api.put(`/tenant-plan-access/${id}`, {
    ...data,
    tenantId,
  });
  return response.data;
};

export const removeTenantPlanAccess = async (id: string, tenantId?: string) => {
  const response = await api.delete(`/tenant-plan-access/${id}`, {
    data: { tenantId },
  });
  return response.data;
};

// ============================================
// TENANT PRICING CONFIG APIs (price matrix: vehicleType × planType → rates)
// ============================================

export const listTenantPricingConfigs = async (tenantId?: string) => {
  const params: Record<string, string> = {};
  if (tenantId) params.tenantId = tenantId;
  const response = await api.get("/tenant-pricing-config", { params });
  return response.data;
};

export const listTenantPricingConfigsByPlanType = async (
  planTypeId: string,
  tenantId?: string,
) => {
  const response = await api.get(
    `/tenant-pricing-config/by-plan-type/${planTypeId}`,
    { params: tenantId ? { tenantId } : undefined },
  );
  return response.data;
};

export const createTenantPricingConfig = async (data: {
  vehicleTypeId: string;
  planTypeId: string;
  rate: Array<{ upToMinutes: number; price: number }>;
  graceMinutes?: number;
  maxCap?: number;
  maxCapPeriod?: "entry" | "day";
  roundingStrategy?: "ceil" | "floor" | "exact";
  tenantId?: string;
}) => {
  const response = await api.post("/tenant-pricing-config", data);
  return response.data;
};

export const bulkUpsertTenantPricingConfig = async (
  configs: Array<{
    vehicleTypeId: string;
    planTypeId: string;
    rate: Array<{ upToMinutes: number; price: number }>;
    graceMinutes?: number;
    maxCap?: number;
    maxCapPeriod?: "entry" | "day";
    roundingStrategy?: "ceil" | "floor" | "exact";
  }>,
  tenantId?: string,
) => {
  const response = await api.post(
    "/tenant-pricing-config/bulk",
    tenantId ? { configs, tenantId } : { configs },
  );
  return response.data;
};

export const updateTenantPricingConfig = async (
  id: string,
  data: {
    rate?: Array<{ upToMinutes: number; price: number }>;
    graceMinutes?: number;
    maxCap?: number;
    maxCapPeriod?: "entry" | "day";
    roundingStrategy?: "ceil" | "floor" | "exact";
  },
  tenantId?: string,
) => {
  const response = await api.put(
    `/tenant-pricing-config/${id}`,
    tenantId ? { ...data, tenantId } : data,
  );
  return response.data;
};

export const deleteTenantPricingConfig = async (
  id: string,
  tenantId?: string,
) => {
  const response = await api.delete(`/tenant-pricing-config/${id}`, {
    data: tenantId ? { tenantId } : undefined,
  });
  return response.data;
};

// ============================================
// MONTHLY PASS APIs
// ============================================

export const listMonthlyPasses = async () => {
  const response = await api.get("/monthly-passes");
  return response.data;
};

export const listMonthlyPassesByVehicle = async (vehicleNumber: string) => {
  const response = await api.get(`/monthly-passes/vehicle/${vehicleNumber}`);
  return response.data;
};

export const createMonthlyPass = async (data: {
  vehicleNumber: string;
  vehicleTypeId?: string;
  vehicleTypeCustom?: string;
  ownerName?: string;
  ownerMobile?: string;
  amount: number;
  discountAmount?: number;
  collectedAmount?: number;
  startDate: string;
  expiryDate: string;
  paymentMethod?: "CASH" | "UPI" | "CARD" | "OTHER";
}) => {
  const response = await api.post("/monthly-passes", data);
  return response.data;
};

export const renewMonthlyPass = async (
  id: string,
  data: {
    vehicleTypeId?: string;
    vehicleTypeCustom?: string;
    amount: number;
    discountAmount?: number;
    collectedAmount?: number;
    startDate: string;
    expiryDate: string;
    paymentMethod?: "CASH" | "UPI" | "CARD" | "OTHER";
  },
) => {
  const response = await api.post(`/monthly-passes/${id}/renew`, data);
  return response.data;
};

export const cancelMonthlyPass = async (id: string) => {
  const response = await api.post(`/monthly-passes/${id}/cancel`);
  return response.data;
};

// ============================================
// USER MANAGEMENT APIs
// ============================================

// Register a new user for a tenant
export const registerUser = async (data: {
  name: string;
  mobile: string;
  password: string;
  role: "ADMIN" | "OPERATOR";
  tenantId: string;
}) => {
  const response = await api.post("/users", data);
  return response.data;
};

// Get list of users for a tenant
export const listUsers = async (tenantId: string) => {
  const response = await api.get("/users", {
    params: { tenantId },
  });
  return response.data;
};

// Get single user details by ID
// export const getUserById = async (userId: string, tenantId: string) => {
//   try {
//     const response = await api.get(`/users/${userId}`, {
//       params: { tenantId },
//     });
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching user details:", error);
//     throw error;
//   }
// };

// Update user status (activate/deactivate)
export const updateUserStatus = async (
  userId: string,
  isActive: boolean,
  tenantId: string,
) => {
  const response = await api.patch(`/users/${userId}/status`, {
    isActive,
    tenantId,
  });
  return response.data;
};

// Reset user password
export const resetUserPassword = async (
  userId: string,
  password: string,
  tenantId: string,
) => {
  const response = await api.patch(`/users/${userId}/password`, {
    password,
    tenantId,
  });
  return response.data;
};

// Get current user profile
export const getMyProfile = async () => {
  const response = await api.get("/user/me/profile");
  return response.data;
};

export async function updateUser(
  userId: string,
  userData: { name: string; mobile: string; role: string },
  tenantId: string,
) {
  return await api.patch(`/users/update/${userId}`, userData, {
    headers: {
      "x-tenant-id": tenantId,
    },
  });
}

// ============================================
// SETTINGS MANAGEMENT APIs
// ============================================
// Get tenant settings
// export const getSettings = async () => {
//   try {
//     const response = await api.get("/settings");
//     return response.data;
//   } catch (error) {
//     console.error("Error fetching settings:", error);
//     throw error;
//   }
// };

// Update tenant settings
// export const updateSettings = async (settingsData: {
//   logoUrl?: string;
//   address?: string;
//   contactNumbers?: string[];
//   receiptTerms?: string[];
//   billTerms?: string[];
//   timezone?: string;
//   currency?: string;
// }) => {
//   try {
//     const response = await api.patch("/settings", settingsData);
//     return response.data;
//   } catch (error) {
//     console.error("Error updating settings:", error);
//     throw error;
//   }
// };

export const SendOnWhatsapp = async (name: string, mobile: string) => {
  const response = await api.post("/utility/send-whatsapp", { name, mobile });
  return response.data;
};

export const sendWhatsAppReceipt = async (parkingEntryId: string, mobileNumber: string) => {
  const response = await api.post("/parking/send-bill/whatsapp", { parkingEntryId, mobileNumber });
  return response.data;
};

// ============================================
// finance
//===============================================

export const getFinanceData = async (
  filter: "today" | "week" | "month" | "custom",
  from?: string,
  to?: string,
) => {
  const response = await api.post("");
  return response.data;
};

export const addPayment = async (data: {
  parkingEntryId: string;
  amount: number;
  method: string;
}) => {
  const response = await api.post("/payments/add", data);
  return response.data;
};

export const getAuditData = async () => {
  const response = await api.get("/audit");
  return (response.data || []).map((log: any) => {
    const action = log.action || "";
    const entity = log.entity || "";
    const performedBy = log.performedBy || {};

    // Map entity+action to frontend category
    let category = "account";
    if (entity === "CloakRoomEntry") category = "cloakRoom";
    else if (entity === "RoadTaxPayment") category = "roadTax";
    else if (action === "CHECK_IN") category = "checkin";
    else if (
      action === "CHECK_OUT" ||
      action === "DRIVER_REASSIGNED_AT_CHECKOUT"
    )
      category = "checkout";
    else if (action === "DISCOUNT_APPLIED") category = "finance";
    else if (entity === "User") category = "user";

    // Build a readable description
    const actionLabel = action.replace(/_/g, " ").toLowerCase();
    const description = `${actionLabel} on ${entity}`;

    // Build details from oldValue/newValue
    const details: string[] = [];
    if (log.newValue?.vehicleNumber) details.push(log.newValue.vehicleNumber);
    if (log.newValue?.finalAmount != null)
      details.push(`₹${log.newValue.finalAmount}`);
    if (log.newValue?.discountAmount)
      details.push(`Discount: ₹${log.newValue.discountAmount}`);

    return {
      id: log._id,
      timestamp: log.timestamp,
      category,
      action: actionLabel,
      description,
      user: performedBy.name || "System",
      details: details.join(" • ") || entity,
    };
  });
};

// ============================================
// CUSTOMER ACCOUNT APIs
// ============================================

import type {
  AccountListParams,
  AccountListResponse,
  CreateAccountPayload,
  TopUpPayload,
  RecordPaymentPayload,
  LedgerResponse,
} from "@/utils/types/account.types";

// List all accounts with filters
export const getAccountList = async (
  params: AccountListParams = {},
): Promise<AccountListResponse> => {
  const response = await api.get("/accounts", { params });
  return response.data;
};

// Get accounts for a specific vehicle
export const getAccountByVehicle = async (vehicleNumber: string) => {
  const response = await api.get(`/accounts/${vehicleNumber}`);
  return response.data;
};

// Create a new prepaid account
export const createCustomerAccount = async (
  vehicleNumber: string,
  data: CreateAccountPayload,
) => {
  const response = await api.post(`/accounts/${vehicleNumber}`, data);
  return response.data;
};

// Top up an existing account
export const topUpAccount = async (
  vehicleNumber: string,
  data: TopUpPayload,
) => {
  const response = await api.patch(`/accounts/${vehicleNumber}`, data);
  return response.data;
};

// Record a payment against an account
export const recordAccountPayment = async (
  vehicleNumber: string,
  data: RecordPaymentPayload,
) => {
  const response = await api.post(`/accounts/${vehicleNumber}/payment`, data);
  return response.data;
};

// Get ledger history for a vehicle's account
export const getAccountLedger = async (
  vehicleNumber: string,
  page: number = 1,
  limit: number = 50,
): Promise<LedgerResponse> => {
  const response = await api.get(`/accounts/${vehicleNumber}/ledger`, {
    params: { page, limit },
  });
  return response.data;
};

// Get top debtors (admin only)
export const getTopDebtors = async (page: number = 1, limit: number = 10) => {
  const response = await api.get("/accounts/top-debtors", {
    params: { page, limit },
  });
  return response.data;
};

// Get top customers (admin only)
export const getTopCustomers = async (page: number = 1, limit: number = 10) => {
  const response = await api.get("/accounts/top-customers", {
    params: { page, limit },
  });
  return response.data;
};

// Reverse a ledger entry (admin only)
export const reverseLedgerEntry = async (
  vehicleNumber: string,
  ledgerEntryId: string,
  reason: string,
) => {
  const response = await api.post(
    `/accounts/${vehicleNumber}/reversal/${ledgerEntryId}`,
    { reason },
  );
  return response.data;
};

// ── Legal Document APIs ───────────────────────────────────────────────────────

export const respondToLegalDocument = async (documentId: string, action: "accepted" | "skipped") => {
  const response = await api.post("/legal/respond", { documentId, action });
  return response.data;
};

export const respondToLegalDocumentsBulk = async (
  documents: { documentId: string; action: "accepted" | "skipped" }[]
) => {
  const response = await api.post("/legal/respond/bulk", { documents });
  return response.data;
};

export const listLegalDocuments = async () => {
  const response = await api.get("/legal");
  return response.data;
};

export const createLegalDocument = async (data: {
  type: "privacy" | "terms" | "security";
  title: string;
  content: string;
  requiresAcceptance?: boolean;
}) => {
  const response = await api.post("/legal", data);
  return response.data;
};

export const updateLegalDocument = async (id: string, data: {
  title?: string;
  content?: string;
  requiresAcceptance?: boolean;
  isActive?: boolean;
}) => {
  const response = await api.patch(`/legal/${id}`, data);
  return response.data;
};

export const listLegalAcceptances = async (params?: { tenantId?: string; documentType?: string }) => {
  const response = await api.get("/legal/acceptances", { params });
  return response.data;
};
